<template>
    <div>
        <l-headline
            :title="title"
            :sub-title="$t(`adminSettings.accessHeadlineSubtitle`)"
        ></l-headline>

        <div class="row">
            <div class="col-md-4">
                <l-select
                    v-model="moduleAccess[module].mode"
                    :options="modeOptions"
                    data-test-id="team-settings-access-mode"
                />
            </div>
            <div class="col-md-4">
                <l-input-typeahead
                    v-if="moduleAccess[module].mode === 'ENABLED_FOR_SELECTED_USERS'"
                    :show-picture="false"
                    :show-checkbox="true"
                    style="margin-top: 0px"
                    :label="``"
                    :placeholder="$t(`adminSettings.accessIncludeLabel`)"
                    :data="rulesIncludeOptions"
                    :on-select="item => addRule('include', item)"
                    :on-input-change="typeAheadOnInputChange"
                    :max-matches="200"
                />
            </div>
            <div class="col-md-4">
                <l-input-typeahead
                    v-if="moduleAccess[module].mode === 'ENABLED_FOR_SELECTED_USERS'"
                    :show-picture="false"
                    :show-checkbox="true"
                    style="margin-top: 0px"
                    :label="``"
                    :placeholder="$t(`adminSettings.accessExcludeLabel`)"
                    :data="rulesExcludeOptions"
                    :on-select="item => addRule('exclude', item)"
                    :on-input-change="typeAheadOnInputChange"
                    :max-matches="200"
                />
            </div>
        </div>

        <div v-if="moduleAccess[module].mode === 'ENABLED_FOR_SELECTED_USERS'">
            <span v-for="item in currentRules('include')" :key="item._id">
                <span class="label label-success ng-binding r-default-margin-s">
                    <span
                        class="clickable"
                        tabindex="0"
                        @keyup.enter="removeRule(item)"
                        @click="removeRule(item)"
                    >
                        <i class="fal fa-check r-default-margin-s"></i>
                        {{ item.name }}
                        <i class="fal fa-trash l-default-margin-s"></i>
                    </span>
                </span>
            </span>
        </div>
        <div v-if="moduleAccess[module].mode === 'ENABLED_FOR_SELECTED_USERS'">
            <span v-for="item in currentRules('exclude')" :key="item._id">
                <span class="label label-danger ng-binding r-default-margin-s">
                    <span
                        class="clickable"
                        tabindex="0"
                        @keyup.enter="removeRule(item)"
                        @click="removeRule(item)"
                    >
                        <i class="fal fa-ban r-default-margin-s"></i>
                        {{ item.name }}
                        <i class="fal fa-trash l-default-margin-s"></i>
                    </span>
                </span>
            </span>
        </div>

        <div v-if="moduleAccess[module].mode != 'DISABLED'">
            <hr />

            <div class="row">
                <div class="col-md-4">
                    <l-input-typeahead
                        :show-picture="false"
                        :show-checkbox="true"
                        :label="``"
                        :placeholder="$t(`adminSettings.accessAdminLabel`)"
                        :data="adminOptions"
                        :on-select="item => addAdmin(item)"
                        :on-input-change="typeAheadOnInputChange"
                        :max-matches="200"
                    />
                </div>
            </div>

            <div>
                <span v-for="(item, index) in moduleAccess[module].admins.users" :key="item._id">
                    <span class="label label-success ng-binding r-default-margin-s">
                        <span
                            class="clickable"
                            tabindex="0"
                            @keyup.enter="removeAdmin('user', index)"
                            @click="removeAdmin('user', index)"
                        >
                            {{ item.name || item.fullname }}
                            <i class="fal fa-trash l-default-margin-s l-default-margin-s"></i>
                        </span>
                    </span>
                </span>
                <span
                    v-for="(item, index) in moduleAccess[module].admins.localTeams"
                    :key="item._id"
                >
                    <span class="label label-success ng-binding r-default-margin-s">
                        <span
                            class="clickable"
                            tabindex="0"
                            @keyup.enter="removeAdmin('localTeam', index)"
                            @click="removeAdmin('localTeam', index)"
                        >
                            {{ item.name }}
                            <i class="fal fa-trash l-default-margin-s l-default-margin-s"></i>
                        </span>
                    </span>
                </span>
            </div>
        </div>

        <hr />

        <div v-show="updateSuccess">
            <l-text-alert
                type="success"
                :text="$t(`adminSettings.accessUpdateSuccess`)"
                data-test-id="team-settings-access-update-success"
            />
        </div>
        <div v-show="updateError">
            <l-text-alert
                type="warning"
                :text="$t(`adminSettings.accessUpdateError`)"
                data-test-id="team-settings-access-update-error"
            />
        </div>
        <div class="row">
            <div class="col-md-offset-8 col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    :disabled="!rights.A_PERM"
                    data-test-id="team-settings-access-update"
                    @click="() => updateGlobalTeam()"
                >
                    {{ $t(`adminSettings.accessUpdateBtn`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LHeadline from "@/components/LHeadline";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSelect from "@/components/LSelect";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LHeadline,
        LInputTypeahead,
        LSelect,
        LTextAlert,
    },
    props: {
        module: {
            type: String,
            default: "",
        },
        moduleAccess: {
            type: Object,
            default: () => ({}),
        },
        title: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            modeOptions: [
                {
                    value: "ENABLED_FOR_COMPANY",
                    text: this.$t(`adminSettings.accessEnabledForEveryone`),
                },
                {
                    value: "ENABLED_FOR_SELECTED_USERS",
                    text: this.$t(`adminSettings.accessEnabledForSelected`),
                },
                {
                    value: "DISABLED",
                    text: this.$t(`adminSettings.accessDisabledForEveryone`),
                },
            ],
            team: {},
            updateSuccess: false,
            updateError: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",

            getCustomRoles: "teams/customrole/list",
            locationList: "users/location/list",
            localTeams: "teams/local/listSortedByName",
        }),
        rulesIncludeOptions() {
            return this.includeOrExcludeOptions("include");
        },
        rulesExcludeOptions() {
            return this.includeOrExcludeOptions("exclude");
        },
        adminOptions() {
            return this.getAdminOptions();
        },
    },
    methods: {
        currentRules(key) {
            return [
                ...(this.moduleAccess?.[this.module]?.[key]?.users ?? []).map((item, index) => {
                    return {
                        _id: item._id,
                        name: item.name || item.fullname,
                        rule: key,
                        type: "user",
                        index,
                    };
                }),
                ...(this.moduleAccess?.[this.module]?.[key]?.localTeams ?? []).map(
                    (item, index) => {
                        return {
                            _id: item._id,
                            name: item.name,
                            rule: key,
                            type: "localTeam",
                            index,
                        };
                    },
                ),
                ...(this.moduleAccess?.[this.module]?.[key]?.locations ?? []).map((item, index) => {
                    return {
                        _id: item._id ? item._id : item,
                        name: item.name ? item.name : item,
                        rule: key,
                        type: "location",
                        index,
                    };
                }),
            ];
        },
        includeOrExcludeOptions(key) {
            const existingUsers = this.moduleAccess?.[this.module]?.[key]?.users ?? [];
            const existingLocalTeams = this.moduleAccess?.[this.module]?.[key]?.localTeams ?? [];
            const existingLocations = this.moduleAccess?.[this.module]?.[key]?.locations ?? [];

            return [
                ...this.usersSearchList
                    .filter(user => !existingUsers.map(p => p._id).includes(user._id))
                    .map(user => ({
                        _id: user._id,
                        type: "user",
                        name: user.fullname,
                    })),
                ...this.teamsSearchList
                    .filter(team => !existingLocalTeams.map(t => t._id).includes(team._id))
                    .map(team => ({
                        _id: team._id,
                        type: "localTeam",
                        name: team.name,
                    })),
                ...(this.locationList || [])
                    .filter(l => !existingLocations.includes(l.location))
                    .map(l => ({
                        _id: l._id,
                        type: "location",
                        name: l.name,
                    })),
            ];
        },
        typeAheadOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        addRule(excludeOrInclude, item) {
            if (!item) {
                return;
            }

            const type = item.type;

            if (type === "user") {
                this.moduleAccess[this.module][excludeOrInclude].users.push(item);
            } else if (type === "location") {
                this.moduleAccess[this.module][excludeOrInclude].locations.push(item);
            } else if (type === "localTeam") {
                this.moduleAccess[this.module][excludeOrInclude].localTeams.push(item);
            }

            this.$forceUpdate();
        },
        removeRule(item) {
            const excludeOrInclude = item.rule;
            const type = item.type;
            const index = item.index;

            if (type === "user") {
                this.moduleAccess[this.module][excludeOrInclude].users.splice(index, 1);
            } else if (type === "location") {
                this.moduleAccess[this.module][excludeOrInclude].locations.splice(index, 1);
            } else if (type === "localTeam") {
                this.moduleAccess[this.module][excludeOrInclude].localTeams.splice(index, 1);
            }

            this.$forceUpdate();
        },

        getAdminOptions() {
            const existingUsers = this.moduleAccess?.[this.module]?.admins?.users ?? [];
            const existingLocalTeams = this.moduleAccess?.[this.module]?.admins?.localTeams ?? [];

            return [
                ...this.usersSearchList
                    .filter(user => !existingUsers.map(p => p._id).includes(user._id))
                    .map(user => ({
                        _id: user._id,
                        type: "user",
                        name: user.fullname,
                    })),
                ...this.teamsSearchList
                    .filter(team => !existingLocalTeams.map(t => t._id).includes(team._id))
                    .map(team => ({
                        _id: team._id,
                        type: "localTeam",
                        name: team.name,
                    })),
            ];
        },

        addAdmin(item) {
            if (!item) {
                return;
            }

            const type = item.type;

            if (type === "user") {
                this.moduleAccess[this.module].admins.users.push(item);
            } else if (type === "localTeam") {
                this.moduleAccess[this.module].admins.localTeams.push(item);
            }

            this.$forceUpdate();
        },

        removeAdmin(type, index) {
            if (type === "user") {
                this.moduleAccess[this.module].admins.users.splice(index, 1);
            } else if (type === "localTeam") {
                this.moduleAccess[this.module].admins.localTeams.splice(index, 1);
            }

            this.$forceUpdate();
        },

        updateGlobalTeam() {
            this.updateSuccess = false;
            this.updateError = false;

            this.$store
                .dispatch({
                    type: "teams/current/update",
                    payload: {
                        scope: "moduleAccess",
                        update: {
                            moduleAccess: this.moduleAccess,
                        },
                    },
                })
                .then(() => {
                    this.updateSuccess = true;
                })
                .catch(() => {
                    this.updateError = true;
                });
        },
    },
};
</script>

<style scoped>
.row > .col-md-3 {
    margin-top: 5px;
}
</style>
